import { CloseOutlined, SettingOutlined } from '@ant-design/icons';
import { Button, Heading, Switch, Tooltip } from '@mm/components';
import { CounterInput } from '@mm/components/src/CounterInput/CounterInput';
import {
  DesktopIcon,
  MobileNotificationIcon,
  EmailClickIcon,
  EmailOpenIcon,
  EmailRed,
  EmailReplyIcon,
  SlackIcon,
  SmsNotificationIcon,
} from '@mm/icons';
import { useCampaignCRUDActions } from '@mm/state/actions/CampaignsCRUDActions';
import {
  CampaignType,
  SoldConfigurationsType,
} from '@mm/state/model/CampaignsModel';
import { useModalActions } from '@mm/state/modules/modalsState/actions';
import { selectCampaignById } from '@mm/state/selectors/campaignSelectors';
import {
  activeChatbotIdSelector,
  activeCompanyUsers,
  selectActiveCompanyId,
} from '@mm/state/selectors/viewSelectors';

import { useFormik } from 'formik';
import { isEqual } from 'lodash';
import React, { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import Checkbox from '../../../../../common/components/Checkbox';

import CollapsibleContentContainer from '../../../../../common/components/CollapsibleContentContainer/CollapsibleContentContainer';
import { errorToast, successToast } from '../../../../../common/helpers/toast';
import { SlideoutMenu as Menu } from '../../components/SlideoutMenu';
import { useSlideoutOnClose } from '../SlideoutEmail/SlideoutEmail';
import css from './AutomationSoLDSettingsSlideout.module.css';
import { ReactComponent as UnicornIcon } from 'assets/zoo/glowing_unicorn.svg';
import classNames from 'classnames';
import { useViewActions } from '@mm/state/actions/viewActions';
import { ReactComponent as DonkeyIcon } from 'assets/zoo/shield_donkey.svg';
import { useResolutionInfo } from '@mm/hooks';
import { MOBILE_THRESHOLD } from '../../../../constants/screenSize';
import { useDesktopNotifications } from '../../../../../common/hooks/use-desktop-notifications/use-desktop-notifications';
import { combinedFeatureFlagsSelector } from '@mm/state/selectors/featureFlagsSelectors';
import { landmineNames } from '@mm/constants/landmine-names';
import { StoreState } from '@mm/state/store-interfaces';
import { useUsersCRUDActions } from '@mm/state/actions/UsersCRUDActions';
import { useConnectSlack } from '../../../../features/beta/modules/Integrations/NativeIntegrations/components/SlackIntegrations/use-connect-slack';
import { PrimaryButton } from '../../../../../common/buttons/PrimaryButton/PrimaryButton';
import { useSOLDNotifications } from './use-SOLD-notifications';
import { SlideOutMenuType } from '@mm/state/interfaces/view';
import { FilterableMultiSelect } from '../../../../../common/components/FilterableMultiSelect/FilterableMultiSelect';
import { equals } from 'lodash/fp';

export type IAutomationSoLDSettingsSlideout = {
  menuType: SlideOutMenuType;
  className?: string;
  campaignId?: number;
};

export const soldConfigInitialValues: SoldConfigurationsType = {
  unicorn: {
    open: 0,
    click: 0,
    reply: 0,
    notifications: {
      rule: { open: 0, click: 0, reply: 0 },
      sms: {
        to: [] as string[],
        content: '',
        active: false,
      },
      desktopPush: {
        to: [] as number[],
        active: false,
      },
      mobilePush: {
        to: [] as number[],
        active: false,
      },
      slack: {
        teamId: '',
        teamName: '',
        assetUrl: null,
        botName: 'Customers.ai',
        sendAsBot: true,
        slackChannelId: '',
        slackChannelName: '',
        content: '' as string | null,
        active: false,
      },
      email: {
        to: [],
        content: '',
        subject: '',
        active: false,
      },
    } as SoldConfigurationsType['unicorn']['notifications'],
  },
  donkey: {
    notOpen: 0,
    notClick: 0,
    notReply: 0,
  },
};

export const AutomationSoLDSettingsSlideout: React.FC<
  IAutomationSoLDSettingsSlideout
  // tslint:disable-next-line:cyclomatic-complexity
> = props => {
  const chatbotId = useSelector(activeChatbotIdSelector);
  const companyId = useSelector(selectActiveCompanyId);
  const { campaignId, menuType } = props;

  const campaign = useSelector(state =>
    selectCampaignById(state, campaignId || -1),
  );

  const companyUsers = useSelector(activeCompanyUsers);

  const getRemappedCompanyUsers = () =>
    companyUsers.map(usr => ({
      id: usr.id,
      name: `${usr.firstName} ${usr.lastName}`,
      key: `${usr.id}`,
    }));

  const [users, setUsers] = useState(getRemappedCompanyUsers());

  useEffect(
    () => {
      const newUsers = getRemappedCompanyUsers();
      setUsers(newUsers);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [JSON.stringify(companyUsers)],
  );

  const usersCRUDActions = useUsersCRUDActions();

  const {
    id: userId,
    userAttributes: { desktopNotifications, hideMobileDownload },
  } = useSelector((state: StoreState) => state.session.session);

  const featureFlags = useSelector(combinedFeatureFlagsSelector);
  const {
    canActivateSmsNotification,
    canActivateSlackNotification,
    canActivateEmailNotification,
    hasSMSNotificationContent,
    hasSMSNotificationPhones,
    hasEmailNotificationContent,
    hasEmailNotificationEmails,
  } = useSOLDNotifications(campaign);

  const safeInitialValues = useMemo(() => {
    let initValues: any = soldConfigInitialValues;

    if (typeof campaign?.soldConfigurations === 'string') {
      if (Object.keys(JSON.parse(campaign?.soldConfigurations)).length) {
        initValues = JSON.parse(campaign?.soldConfigurations);
      }
    } else if (
      campaign?.soldConfigurations &&
      Object.keys(campaign.soldConfigurations).length
    ) {
      initValues = campaign.soldConfigurations;
    }

    return initValues;
  }, [campaign, users]);

  const [slack, setSlack] = useState(
    campaign?.soldConfigurations?.unicorn?.notifications?.slack?.active,
  );
  const [email, setEmail] = useState(
    campaign?.soldConfigurations?.unicorn?.notifications?.email?.active,
  );
  const [sms, setSms] = useState(
    campaign?.soldConfigurations?.unicorn?.notifications?.sms?.active,
  );
  const { connectSlack, retrieveSlackAccounts, slackAccounts } =
    useConnectSlack();

  const formik = useFormik<typeof soldConfigInitialValues>({
    initialValues: safeInitialValues,
    onSubmit: async values => {},
  });

  const {
    openDesktopNotificationsModal,
    notificationServiceWorkerAdded,
    browserNotificationPermissionsStatus,
    oneSignalPlayerId,
    isUnsubscribedFromNotifications,
  } = useDesktopNotifications();

  useEffect(() => {
    if (oneSignalPlayerId === null) return;
    const desktopDevices = desktopNotifications?.desktopDevices || [];
    const thisDeviceSaved = desktopDevices.includes(oneSignalPlayerId);
    if (!thisDeviceSaved) {
      usersCRUDActions.updateUserAttributes(companyId, userId, {
        desktopNotifications: {
          ...desktopNotifications,
          desktopDevices: [oneSignalPlayerId, ...desktopDevices],
        },
      });
    }
  }, [
    notificationServiceWorkerAdded,
    browserNotificationPermissionsStatus,
    oneSignalPlayerId,
    isUnsubscribedFromNotifications,
  ]);

  const active =
    formik.values.stopSendingAfterReply ||
    Object.values(formik.values.unicorn).some(value => value > 0) ||
    Object.values(formik.values.donkey).some(value => value > 0);

  const [busy, setBusy] = useState(false);

  const campaignCRUDActions = useCampaignCRUDActions();

  const isSaveDisabled = busy;

  // Do all the needed transformations to the formik values before sending to BE
  const withNotifications = (sold: typeof soldConfigInitialValues) => {
    const clone = JSON.parse(JSON.stringify(sold));

    delete clone.unicorn.notifications.sms;
    delete clone.unicorn.notifications.slack;

    clone.unicorn.notifications.sms = {
      ...campaign?.soldConfigurations?.unicorn?.notifications?.sms,
      active: sms,
    };
    clone.unicorn.notifications.slack = {
      ...campaign?.soldConfigurations?.unicorn?.notifications?.slack,
      active: slack,
    };

    clone.unicorn.notifications.email = {
      ...campaign?.soldConfigurations?.unicorn?.notifications?.email,
      active: email,
    };

    return clone as SoldConfigurationsType;
  };

  const onSave = async () => {
    setBusy(true);
    try {
      if (campaignId) {
        await campaignCRUDActions.updateCampaign(
          {
            id: campaignId,
            soldConfigurations: withNotifications(formik.values),
          } as unknown as CampaignType,
          chatbotId,
        );

        successToast('Your changes have been saved.', 'top-left');

        closeSlideout();
      }
    } catch (ee) {
      errorToast('Something went wrong', 'top-left');
    } finally {
      setBusy(false);
    }
  };

  const modalActions = useModalActions();

  const onBeforeClose = () => {
    if (
      !isEqual(withNotifications(formik.values), campaign?.soldConfigurations)
    ) {
      return new Promise((resolve, reject) => {
        modalActions.openGeneralConfirmModal({
          headerText: 'You have unsaved changes',
          messageText: 'Are you sure that you want to leave without saving?',
          onCloseHandler: ok => {
            if (ok) {
              resolve(true);
            } else {
              reject();
            }
          },
          okButtonText: "Yes I'm sure",
          cancelButtonText: 'Cancel',
        });
      });
    }

    return Promise.resolve(true);
  };

  const close = async () => {
    try {
      await onBeforeClose();
      closeSlideout();
    } catch (err) {}
  };

  const viewActions = useViewActions();

  const { setRef, close: closeSlideout } = useSlideoutOnClose(
    onBeforeClose,
    menuType,
  );

  const companyUserIdsToNotify: number[] = [];

  useEffect(
    () => {
      const newUsers = getRemappedCompanyUsers();
      setUsers(newUsers);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [JSON.stringify(companyUsers), JSON.stringify(companyUserIdsToNotify)],
  );

  const updateMobileCompanyUsersToNotify = (keys: string[]) => {
    if (keys.length === 0) {
      formik.setFieldValue('unicorn.notifications.mobilePush.active', false);
    }
    const keysAsNumbers = keys.map(k => Number(k));
    if (
      !equals(
        keysAsNumbers,
        formik.values.unicorn.notifications?.mobilePush?.to,
      )
    ) {
      formik.setFieldValue(
        'unicorn.notifications.mobilePush.to',
        keysAsNumbers,
      );
    }
  };

  const updateDesktopCompanyUsersToNotify = (keys: string[]) => {
    if (keys.length === 0) {
      formik.setFieldValue('unicorn.notifications.desktopPush.active', false);
    }
    const keysAsNumbers = keys.map(k => Number(k));
    if (
      !equals(
        keysAsNumbers,
        formik.values.unicorn.notifications?.desktopPush?.to,
      )
    ) {
      formik.setFieldValue(
        'unicorn.notifications.desktopPush.to',
        keysAsNumbers,
      );
    }
  };

  const { screenWidth } = useResolutionInfo();
  const isMobile = screenWidth <= MOBILE_THRESHOLD;

  const getSwitchCss = (type?: string) => {
    let isActive = false;

    switch (type) {
      case 'sms':
        isActive = !!sms;
        break;
      case 'slack':
        isActive = !!slack;
        break;
      case 'reply':
        isActive = !!formik.values.stopSendingAfterReply;
        break;
      case 'desktop':
        isActive = !!formik.values.unicorn.notifications?.desktopPush?.active;
        break;
      case 'mobile':
        isActive = !!formik.values.unicorn.notifications?.mobilePush?.active;
        break;
      case 'email':
        isActive = !!email;
        break;
      default:
        return;
    }

    return {
      container: isActive ? css.contentSwitchActive : css.contentSwitchInactive,
      thumb: isActive ? css.contentThumbActive : css.contentThumbInactive,
      label: isActive
        ? css.contentSwitchLabelActive
        : css.contentSwitchLabelInactive,
    };
  };

  useEffect(() => {
    retrieveSlackAccounts();
  }, []);

  const handleDesktopNotificationChange = () => {
    if (
      !formik.values?.unicorn?.notifications?.desktopPush?.active &&
      (!notificationServiceWorkerAdded || isUnsubscribedFromNotifications)
    ) {
      if (!featureFlags?.desktopNotifications) {
        return modalActions.openUniversalUpgradeLandmineModal({
          landmineName:
            landmineNames['MY_ACCOUNT__ENABLE-DESKTOP-NOTIFICATIONS'],
          feature: 'desktopNotifications',
        });
      }

      const callBack = () => {
        if (!formik.values?.unicorn?.notifications?.desktopPush?.to?.length) {
          formik.setFieldValue('unicorn.notifications.desktopPush.to', [
            ...users.map(u => u.id),
          ]);
        }
        formik.setFieldValue(
          'unicorn.notifications.desktopPush.active',
          !formik.values?.unicorn?.notifications?.desktopPush?.active,
        );
      };
      openDesktopNotificationsModal(callBack);
    } else {
      if (!formik.values?.unicorn?.notifications?.desktopPush?.to?.length) {
        formik.setFieldValue('unicorn.notifications.desktopPush.to', [
          ...users.map(u => u.id),
        ]);
      }
      formik.setFieldValue(
        'unicorn.notifications.desktopPush.active',
        !formik.values?.unicorn?.notifications?.desktopPush?.active,
      );
    }
  };

  const renderSmsSwitch = (
    <Switch
      checked={!!sms}
      disabled={!canActivateSmsNotification}
      onChange={() => setSms(!sms)}
      css={{
        container: getSwitchCss('sms')?.container,

        thumb: getSwitchCss('sms')?.thumb,
        label: getSwitchCss('sms')?.label,
      }}
    />
  );

  return (
    <Menu className={css.container} setRef={setRef}>
      <div className={css.header}>
        <Heading size={3}>
          {isMobile
            ? 'SoLD Settings'
            : 'SoLD (Signs of Life Detector) Settings'}
        </Heading>
        <Switch
          checked={active}
          label={active ? 'active' : 'inactive'}
          disabled={isEqual(formik.values, soldConfigInitialValues)}
          onChange={() => {
            formik.setValues(soldConfigInitialValues);
          }}
          css={{
            container: css.switch,
            thumb: active ? css.thumbActive : css.thumbInactive,
            label: active ? css.switchLabelActive : css.switchLabelInactive,
          }}
        />

        <Button
          css={{ container: css.save }}
          onClick={onSave}
          disabled={isSaveDisabled}
        >
          Save
        </Button>
        <span className={css.close} onClick={close}>
          <CloseOutlined />
        </span>
      </div>
      <Menu.Content className={css.content}>
        <section>
          <CollapsibleContentContainer
            orderNumber="1"
            containerName="Unicorns"
            stepperStylingPlatform="sold"
            useCircleNumberStyle
            expandedOnMount
            hideTitleBorder
            className={css.dropdown}
          >
            <div className={css.collapsibleContent}>
              <div className={css.contentLeftColumn}>
                <div className={css.subtitle}>
                  Classify a lead as a Unicorn if they...
                </div>

                <div className={css.filterRow}>
                  <Checkbox
                    isChecked={!!formik.values.unicorn.open}
                    onToggle={() => {
                      formik.setFieldValue(
                        'unicorn.open',
                        formik.values.unicorn.open ? 0 : 1,
                      );
                    }}
                  />
                  <EmailOpenIcon
                    color={formik.values.unicorn.open ? '#597EF7' : undefined}
                    className={css.filterIcon}
                  />
                  <span>Open at least</span>
                  <CounterInput
                    min={0}
                    value={formik.values.unicorn.open}
                    onChange={v => formik.setFieldValue('unicorn.open', v)}
                    css={{
                      container: css.counterInputContainer,
                      input: css.counterInputInput,
                    }}
                  />
                  <span>times</span>
                </div>

                <div className={css.orRow}>or</div>

                <div className={css.filterRow}>
                  <Checkbox
                    isChecked={!!formik.values.unicorn.click}
                    onToggle={() => {
                      formik.setFieldValue(
                        'unicorn.click',
                        formik.values.unicorn.click ? 0 : 1,
                      );
                    }}
                  />
                  <EmailClickIcon
                    className={css.filterIcon}
                    color={formik.values.unicorn.click ? '#597EF7' : undefined}
                  />
                  <span>Click at least</span>
                  <CounterInput
                    min={0}
                    value={formik.values.unicorn.click}
                    onChange={v => formik.setFieldValue('unicorn.click', v)}
                    css={{
                      container: css.counterInputContainer,
                      input: css.counterInputInput,
                    }}
                  />
                  <span>times</span>
                </div>

                <div className={css.orRow}>or</div>

                <div className={css.filterRow}>
                  <Checkbox
                    isChecked={!!formik.values.unicorn.reply}
                    onToggle={() => {
                      formik.setFieldValue(
                        'unicorn.reply',
                        formik.values.unicorn.reply ? 0 : 1,
                      );
                    }}
                  />
                  <EmailReplyIcon
                    className={css.filterIcon}
                    color={formik.values.unicorn.reply ? '#597EF7' : undefined}
                  />
                  <span>Reply at least</span>
                  <CounterInput
                    min={0}
                    value={formik.values.unicorn.reply}
                    onChange={v => formik.setFieldValue('unicorn.reply', v)}
                    css={{
                      container: css.counterInputContainer,
                      input: css.counterInputInput,
                    }}
                  />
                  <span>times</span>
                </div>

                <div className={css.replyRow}>
                  <Switch
                    checked={!!formik.values.stopSendingAfterReply}
                    label={''}
                    onChange={() => {
                      formik.setFieldValue(
                        'stopSendingAfterReply',
                        !formik.values.stopSendingAfterReply,
                      );
                    }}
                    css={{
                      container: getSwitchCss('reply')?.container,

                      thumb: getSwitchCss('reply')?.thumb,
                      label: getSwitchCss('reply')?.label,
                    }}
                  />
                  <span className={css.optionText}>
                    Remove leads from Automation when they reply
                  </span>
                </div>
              </div>
              <div className={css.contentRightColumn}>
                <UnicornIcon />
              </div>
            </div>

            <div className={css.block}>
              <div className={css.subtitle}>Notify me on...</div>
              <div className={css.notificationRow}>
                <Tooltip
                  title={
                    !canActivateSlackNotification
                      ? 'You have to set up Workspace, Channel and Message in order to activate'
                      : ''
                  }
                >
                  <Switch
                    checked={!!slack}
                    disabled={!canActivateSlackNotification}
                    onChange={() => setSlack(!slack)}
                    css={{
                      container: getSwitchCss('slack')?.container,

                      thumb: getSwitchCss('slack')?.thumb,
                      label: getSwitchCss('slack')?.label,
                    }}
                  />
                </Tooltip>
                <SlackIcon className={css.notificationIcon} />
                <div>Slack</div>
                {!slackAccounts?.length ? (
                  <PrimaryButton
                    onClick={connectSlack}
                    className={css.secondaryButton}
                  >
                    Connect
                  </PrimaryButton>
                ) : (
                  <SettingOutlined
                    onClick={() =>
                      viewActions.showSlideoutMenu(
                        'automationSoLDSlackNotificationSettings',
                        { campaignId },
                      )
                    }
                    className={css.notificatoinSettingIcon}
                  />
                )}
              </div>

              <div className={css.notificationRow}>
                <Tooltip
                  title={
                    !canActivateEmailNotification ? (
                      <div>
                        {' '}
                        You have to enter a{' '}
                        {!canActivateEmailNotification
                          ? 'message and recipient email address'
                          : !hasEmailNotificationContent
                          ? 'message'
                          : 'recipient email address'}{' '}
                        in order to activate
                      </div>
                    ) : (
                      ''
                    )
                  }
                >
                  <Switch
                    checked={!!email}
                    onChange={() => setEmail(!email)}
                    disabled={!canActivateEmailNotification}
                    css={{
                      container: getSwitchCss('email')?.container,

                      thumb: getSwitchCss('email')?.thumb,
                      label: getSwitchCss('email')?.label,
                    }}
                  />
                </Tooltip>
                <EmailRed
                  className={css.notificationIcon}
                  style={{ width: '20px' }}
                />
                <div>Email</div>
                <SettingOutlined
                  onClick={() =>
                    viewActions.showSlideoutMenu(
                      'automationSoLDEmailNotificationSettings',
                      { campaignId },
                    )
                  }
                  className={css.notificatoinSettingIcon}
                />
              </div>

              <div className={css.notificationRow}>
                {hasSMSNotificationPhones ? (
                  renderSmsSwitch
                ) : (
                  <Tooltip
                    title={
                      <div>
                        {' '}
                        You have to enter a{' '}
                        {[
                          !hasSMSNotificationContent && 'text message',
                          !hasSMSNotificationPhones && 'phone number',
                        ]
                          .filter(Boolean)
                          .join(' and a')}{' '}
                        in order to activate
                      </div>
                    }
                  >
                    renderSmsSwitch
                  </Tooltip>
                )}

                <SmsNotificationIcon
                  className={classNames(css.notificationIcon, css.sms)}
                />
                <span className={css.notificationTitle}>SMS</span>

                <SettingOutlined
                  onClick={() =>
                    viewActions.showSlideoutMenu(
                      'automationSoLDSmsNotificationSettings',
                      { campaignId },
                    )
                  }
                  className={css.notificatoinSettingIcon}
                />
              </div>

              <div className={css.notificationRow}>
                <Switch
                  checked={
                    !!formik.values?.unicorn?.notifications?.mobilePush?.active
                  }
                  onChange={() => {
                    if (
                      !formik.values?.unicorn?.notifications?.mobilePush?.active
                    ) {
                      !hideMobileDownload &&
                        modalActions.openMobileDownloadModal();

                      if (
                        !formik.values?.unicorn?.notifications?.mobilePush?.to
                          ?.length
                      ) {
                        formik.setFieldValue(
                          'unicorn.notifications.mobilePush.to',
                          [...users.map(u => u.id)],
                        );
                      }
                    }

                    formik.setFieldValue(
                      'unicorn.notifications.mobilePush.active',
                      !formik.values?.unicorn?.notifications?.mobilePush
                        ?.active,
                    );
                  }}
                  css={{
                    container: getSwitchCss('mobile')?.container,
                    thumb: getSwitchCss('mobile')?.thumb,
                    label: getSwitchCss('mobile')?.label,
                  }}
                />
                <MobileNotificationIcon className={css.notificationIcon} />
                <div>Mobile Push</div>
                {!!formik.values?.unicorn?.notifications?.mobilePush
                  ?.active && (
                  <FilterableMultiSelect
                    className={css.filterableSelect}
                    heading="Select Users"
                    items={users}
                    disableDeleting={false}
                    selectedKeys={(
                      formik.values.unicorn.notifications?.mobilePush?.to || []
                    ).map(k => String(k))}
                    onChange={updateMobileCompanyUsersToNotify}
                    showBadge={false}
                    showSearch={false}
                    isOnChangeInOnClick={true}
                  />
                )}
              </div>
              <div className={css.notificationRow}>
                <Switch
                  checked={
                    !!formik.values?.unicorn?.notifications?.desktopPush?.active
                  }
                  onChange={handleDesktopNotificationChange}
                  css={{
                    container: getSwitchCss('desktop')?.container,
                    thumb: getSwitchCss('desktop')?.thumb,
                    label: getSwitchCss('desktop')?.label,
                  }}
                />

                <DesktopIcon className={css.notificationIcon} />
                <div>Desktop Push</div>

                {!!formik.values?.unicorn?.notifications?.desktopPush
                  ?.active && (
                  <FilterableMultiSelect
                    className={css.filterableSelect}
                    heading="Select Users"
                    items={users}
                    disableDeleting={false}
                    selectedKeys={(
                      formik.values.unicorn.notifications?.desktopPush?.to || []
                    ).map(k => String(k))}
                    onChange={updateDesktopCompanyUsersToNotify}
                    showBadge={false}
                    showSearch={false}
                    isOnChangeInOnClick={true}
                  />
                )}
              </div>

              <div>
                {formik.values?.unicorn?.notifications?.desktopPush?.active &&
                  browserNotificationPermissionsStatus &&
                  browserNotificationPermissionsStatus !== 'granted' && (
                    <div className={css.desktopNotificationsBlocked}>
                      <i className="fas fa-exclamation-triangle" /> You have
                      blocked web push notification permissions for this
                      browser. Check out{' '}
                      <a
                        href="https://customers.ai/help/article/xjrvxif08a-how-to-unblock-notifications-permissions-on-your-browser"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        this help doc
                      </a>{' '}
                      for instructions on unblocking notification permissions.
                    </div>
                  )}
              </div>
            </div>
          </CollapsibleContentContainer>
        </section>

        <section className={css.dropdown}>
          <CollapsibleContentContainer
            orderNumber="2"
            containerName="Donkeys (Helps reduce spam complaints)"
            stepperStylingPlatform="sold"
            useCircleNumberStyle
            hideTitleBorder
            className={css.dropdown}
          >
            <div className={css.collapsibleContent}>
              <div className={css.contentLeftColumn}>
                <div className={css.subtitle}>Unsubscribe leads if they...</div>

                <div className={css.filterRow}>
                  <Checkbox
                    isChecked={!!formik.values.donkey.notOpen}
                    onToggle={() => {
                      formik.setFieldValue(
                        'donkey.notOpen',
                        formik.values.donkey.notOpen ? 0 : 1,
                      );
                    }}
                  />
                  <EmailOpenIcon
                    color={formik.values.donkey.notOpen ? '#597EF7' : undefined}
                    className={css.filterIcon}
                  />
                  <span>Do not open after</span>
                  <CounterInput
                    min={0}
                    value={formik.values.donkey.notOpen}
                    onChange={v => formik.setFieldValue('donkey.notOpen', v)}
                    css={{
                      container: css.counterInputContainer,
                      input: css.counterInputInput,
                    }}
                  />
                  <span>emails</span>
                </div>

                <div className={css.orRow}>or</div>

                <div className={css.filterRow}>
                  <Checkbox
                    isChecked={!!formik.values.donkey.notClick}
                    onToggle={() => {
                      formik.setFieldValue(
                        'donkey.notClick',
                        formik.values.donkey.notClick ? 0 : 1,
                      );
                    }}
                  />
                  <EmailClickIcon
                    className={css.filterIcon}
                    color={
                      formik.values.donkey.notClick ? '#597EF7' : undefined
                    }
                  />
                  <span>Do not click after</span>
                  <CounterInput
                    min={0}
                    value={formik.values.donkey.notClick}
                    onChange={v => formik.setFieldValue('donkey.notClick', v)}
                    css={{
                      container: css.counterInputContainer,
                      input: css.counterInputInput,
                    }}
                  />
                  <span>emails</span>
                </div>
              </div>
              <div className={css.contentRightColumnDonkey}>
                <DonkeyIcon />
              </div>
            </div>
          </CollapsibleContentContainer>
        </section>
        {/* Note: Temporary commented out till we implement it*/}
        {/* <section className={css.disabled}>
          <CollapsibleContentContainer
            orderNumber="3"
            containerName="Auto-Intro to Sales Team"
            stepperStylingPlatform="sold"
            expandedOnMount={false}
            useCircleNumberStyle
            hideTitleBorder
          />
        </section> */}
      </Menu.Content>
    </Menu>
  );
};
